import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@clerk/react";
import type { RoomSnapshot, SourceSnapshot, UserFavorite, UserRoom } from "@tuit/shared-types";
import {
  fetchSnapshot,
  fetchFavorites,
  fetchUserRooms,
  addFavorite,
  removeFavorite,
  type SnapshotResult,
} from "../api.js";
import { useReadState } from "../state/readState.js";
import { userConfigurationStorage } from "../userConfigurationStorage.js";

const EMPTY_DEDUP = {
  suppressed: new Set<string>(),
  alsoIn: new Map<string, string[]>(),
};

function buildDedupInfo(snap: RoomSnapshot) {
  const suppressed = new Set<string>();
  const alsoIn = new Map<string, string[]>();
  for (const section of snap.sourceSnapshots) {
    for (const item of section.items) {
      if (item.duplicateOf !== null) {
        suppressed.add(item.id);
        const existing = alsoIn.get(item.url) ?? [];
        existing.push(section.title);
        alsoIn.set(item.url, existing);
      }
    }
  }
  return { suppressed, alsoIn };
}

export function useRoomData() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const isHomeRoom = !(params.roomSlug as string | undefined);
  const roomId = isHomeRoom
    ? "home"
    : (params.roomSlug as string).replace(/^~/, "");

  const { isSignedIn } = useAuth();
  const { readIds, markRead } = useReadState();

  const [fetchResult, setFetchResult] = useState<SnapshotResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [favorites, setFavorites] = useState<UserFavorite[]>([]);
  const [userRooms, setUserRooms] = useState<UserRoom[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [customOrder, setCustomOrder] = useState<string[] | null>(null);

  const fetchGroupRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isSignedIn) {
      setFavorites([]);
      setUserRooms([]);
      return;
    }
    fetchFavorites().then(setFavorites).catch(() => {});
    fetchUserRooms().then(setUserRooms).catch(() => {});
  }, [isSignedIn]);

  useEffect(() => {
    const key = roomId;
    fetchGroupRef.current = key;
    setIsLoading(true);
    fetchSnapshot(roomId).then((result) => {
      if (fetchGroupRef.current !== key) return;
      setFetchResult(result);
      setIsLoading(false);
    });
  }, [roomId]);

  useEffect(() => {
    setCustomOrder(userConfigurationStorage.roomOrder.get(roomId));
  }, [roomId]);

  const snapshot = fetchResult?.data ?? null;

  const { suppressed, alsoIn } = useMemo(
    () => (snapshot ? buildDedupInfo(snapshot) : EMPTY_DEDUP),
    [snapshot],
  );

  const sortedSections = useMemo<SourceSnapshot[]>(() => {
    const sections = snapshot?.sourceSnapshots ?? [];
    const priority = (s: SourceSnapshot) => {
      const hasUnread = s.items.some(
        (item) => !suppressed.has(item.id) && !readIds.has(item.id),
      );
      if (hasUnread) return 0;
      if (s.fetchStatus !== "error") return 1;
      return 2;
    };
    return [...sections].sort((a, b) => priority(a) - priority(b));
  }, [snapshot, suppressed, readIds]);

  const displayedSections = useMemo(() => {
    if (!customOrder) return sortedSections;
    const allIds = sortedSections.map((s) => s.sourceId);
    const merged = [
      ...customOrder.filter((id) => allIds.includes(id)),
      ...allIds.filter((id) => !customOrder.includes(id)),
    ];
    return [...sortedSections].sort(
      (a, b) => merged.indexOf(a.sourceId) - merged.indexOf(b.sourceId),
    );
  }, [sortedSections, customOrder]);

  const filteredSections = useMemo(() => {
    if (!searchQuery) return displayedSections;
    const q = searchQuery.toLowerCase();
    return displayedSections
      .map((s) => ({
        ...s,
        items: s.items.filter((item) => item.title.toLowerCase().includes(q)),
      }))
      .filter((s) => s.items.length > 0);
  }, [displayedSections, searchQuery]);

  const matchCount = searchQuery
    ? filteredSections.reduce((n, s) => n + s.items.length, 0)
    : null;

  function handleSwap(srcId: string, dropId: string) {
    const allIds = sortedSections.map((s) => s.sourceId);
    const base = customOrder ?? allIds;
    const merged = [
      ...base.filter((id) => allIds.includes(id)),
      ...allIds.filter((id) => !base.includes(id)),
    ];
    const next = [...merged];
    const ai = next.indexOf(srcId);
    const bi = next.indexOf(dropId);
    if (ai === -1 || bi === -1) return;
    const tmp = next[ai]!;
    next[ai] = next[bi]!;
    next[bi] = tmp;
    userConfigurationStorage.roomOrder.set(roomId, next);
    setCustomOrder(next);
  }

  async function handleFavoriteToggle(
    toggledRoomId: string,
    roomType: "system" | "user",
    isFavorited: boolean,
  ) {
    if (isFavorited) {
      await removeFavorite(toggledRoomId);
      setFavorites(favorites.filter((f) => f.roomId !== toggledRoomId));
    } else {
      await addFavorite(toggledRoomId, roomType);
      const newFavs = await fetchFavorites();
      setFavorites(newFavs);
    }
  }

  function handleGroupChange(groupId: string | null) {
    if (!groupId) {
      navigate({ to: "/" });
      return;
    }
    navigate({ to: "/$roomSlug", params: { roomSlug: `~${groupId}` } });
  }

  function handleSearch(query: string) {
    setSearchQuery(query);
  }

  function handleSearchClear() {
    setSearchQuery("");
  }

  return {
    isHomeRoom,
    roomId,
    snapshot,
    isLoading,
    favorites,
    userRooms,
    searchQuery,
    customOrder,
    suppressed,
    alsoIn,
    sortedSections,
    displayedSections,
    filteredSections,
    matchCount,
    readIds,
    markRead,
    handleSwap,
    handleFavoriteToggle,
    handleGroupChange,
    handleSearch,
    handleSearchClear,
  };
}
